/**
 * exportGlb — the egress half of ADR-008 §2(i): the modified model leaves as a
 * binary GLB carrying the POST-GRAFT TRUTH — original geometry and materials,
 * the augmented skeleton (grafted digits with their pose-independent inverse
 * binds, seated by bindGraftedDigits), and the given kernel clips baked as glTF
 * animations. ONE responsibility: Object3D + ClipData[] → valid single-scene
 * GLB bytes. The round-trip proof obligation lives in the app's
 * export-roundtrip test (ADR-008 §9).
 *
 * Fail-loud line (LAWS): GLTFExporter's own posture on an unresolvable
 * animation track is console.warn-and-skip — a file that SHIPS with limbs
 * silently unanimated. Every track is therefore pre-resolved by name against
 * the exported subtree (the viewport's own binding check, applied at the
 * seam), and any miss refuses the whole export with the track named. Morph
 * tracks are unresolvable by construction today (toThreeClip emits them with
 * no node prefix) and refuse with that explanation.
 *
 * Viewport debris: hand markers are parented to BONES inside the model subtree
 * (every disc, ring and cloud app/viewport/hand-markers mints is stamped). They are
 * detached for the write and restored after, and reported in `excluded` —
 * a debug overlay must never ship inside a user's file, and never silently.
 *
 * The caller owns POSE: node TRS is read at parse time, so the app must
 * present rests (pause the mixer) before calling — the bind matrices
 * themselves are safe either way (composed from worldBind, not matrixWorld).
 *
 * Node note: GLTFExporter's binary path reads its output Blob through
 * FileReader, which Node lacks; a minimal shim is installed for the duration
 * of the write when (and only when) the global is absent, so the ADR-008 §9
 * round-trip proof can run headless in CI.
 *
 * Depends on: three (+ GLTFExporter addon), kernel (ClipData), to-three-clip.
 * Depended on by: app export door, export-roundtrip test.
 */
import { Object3D, PropertyBinding, SkinnedMesh } from 'three';
import { GLTFExporter } from 'three/addons/exporters/GLTFExporter.js';
import { SpringDeclarationRefusal, toVrmcSpringBone, VRMC_SPRING_BONE, type ClipData, type SpringDeclaration } from '@harryhausen/kernel';
import { toThreeClip } from './to-three-clip.js';
import { isViewportDebris, viewportDebrisLabel } from './viewport-debris.js';
import { GlbExportError, glbJson, installFileReaderShim } from './glb-internals.js';
import { exactSumSkinWeights } from './skin-weight-hygiene.js';
import { sparsifyMorphTargets, type SparsifyReport } from './sparse-morphs.js';
import { SURFACE_MORPH_PREFIX } from './hinge/graft-crease-morph.js';

// The error shape predates glb-internals; re-exported so the public import
// path (`export-glb` via the package index) is unchanged.
export { GlbExportError } from './glb-internals.js';

export interface ExportGlbOptions {
  /** Kernel clips to bake as glTF animations (converted via toThreeClip). */
  readonly clips?: readonly ClipData[];
  /**
   * Spring chains to declare (ADR-038), written as the standard
   * `VRMC_springBone` 1.0 extension at the file root, keyed by node index.
   * Declarations speak the names the FILE speaks (`Tail3`); a name the written
   * subtree does not carry refuses the export by name.
   */
  readonly springs?: readonly SpringDeclaration[];
  /**
   * ADR-038 J2: extra provenance per spring NAME, merged into that spring's
   * `extras.poqpoq` beside the declaration's own (`spring`) — the soft-tissue
   * region surface rides here as `jiggle`. A name no spring carries refuses.
   */
  readonly springExtras?: Readonly<Record<string, Readonly<Record<string, unknown>>>>;
}

export interface ExportGlbResult {
  readonly glb: ArrayBuffer;
  /** Viewport debris found INSIDE the model subtree and left out of the file. */
  readonly excluded: readonly string[];
  /** Baked animations as written: name + track count, all resolved by name. */
  readonly animations: readonly { name: string; tracks: number }[];
  /**
   * Grafted bones written under their CANONICAL names (`X_graft` → `X`), with
   * baked track targets rewritten to match. The file is the product: canonical
   * Mixamo-style finger names mean the export re-imports (here or anywhere) as
   * a first-class fingered rig — Hand Studio sees a hand that already has
   * children and stands down, and any mapper recognizes the digits. The LIVE
   * scene keeps its session names; renames are restored after the write.
   */
  readonly renamed: readonly { from: string; to: string }[];
  /**
   * Skin-weight hygiene applied at the write (skin-weight-hygiene.ts): how
   * many vertices were moved to an exact float32 sum of 1 so the loader's
   * normalization is a bit-exact no-op, the largest single move, and how
   * many vertices could not be (zero-total — the loader's own case).
   */
  readonly weightHygiene: { readonly vertices: number; readonly largestNudge: number; readonly unreachable: number };
  /** ADR-038 §14.5: the `surface.*` morph targets rewritten sparse, and the BIN before/after. */
  readonly sparseMorphs: SparsifyReport;
}

// Debris identity lives in ONE place (viewport-debris.ts) — this door and
// mesh discovery must agree on what to ignore, and two copies of the names
// is exactly how they'd drift (Codex P1s, #98).

/**
 * Export `root` (the loaded model subtree — never the viewport Scene) as a
 * binary GLB with `options.clips` baked in. See the module header for the full
 * contract. Throws {@link GlbExportError}; never returns a partial file.
 */
export async function exportGlb(root: Object3D, options: ExportGlbOptions = {}): Promise<ExportGlbResult> {
  // --- canonical-name plan: `X_graft` → `X` in the WRITTEN file only ---
  const allNames = new Set<string>();
  const toRename: Object3D[] = [];
  root.traverse((node) => {
    allNames.add(node.name);
    if (node.name.endsWith('_graft')) toRename.push(node);
  });
  const renameIssues: string[] = [];
  const plan = toRename.map((node) => {
    const to = node.name.slice(0, -'_graft'.length);
    if (allNames.has(to)) {
      // A rig that already carries the canonical name did not need a graft
      // under a suffix — this is a wiring surprise, refused, never a silent
      // duplicate-name file.
      renameIssues.push(`cannot canonicalize "${node.name}" — the subtree already has a node named "${to}"`);
    }
    return { node, from: node.name, to };
  });
  if (renameIssues.length > 0) throw new GlbExportError(renameIssues);

  const stripGraft = (target: string): string => (target.endsWith('_graft') ? target.slice(0, -'_graft'.length) : target);
  const clips = (options.clips ?? []).map((c) =>
    toThreeClip({ ...c, tracks: c.tracks.map((t) => ({ ...t, target: stripGraft(t.target) })) }),
  );

  for (const { node, to } of plan) node.name = to;
  try {
    // --- every track must bind, by name, inside the exported subtree ---
    const issues: string[] = [];
    for (const clip of clips) {
      for (const track of clip.tracks) {
        const { nodeName } = PropertyBinding.parseTrackName(track.name);
        if (nodeName === undefined || nodeName === '') {
          issues.push(
            `clip "${clip.name}": track "${track.name}" has no node prefix (a morph track from ` +
              `toThreeClip) — it would bind to the export root and animate nothing; give morph ` +
              `tracks their mesh node name before export`,
          );
          continue;
        }
        if (PropertyBinding.findNode(root, nodeName) === null) {
          issues.push(`clip "${clip.name}": track "${track.name}" resolves to no node in the exported subtree`);
        }
      }
    }
    if (issues.length > 0) throw new GlbExportError(issues);

    // --- detach viewport debris riding on bones; restore whatever happens ---
    const debris: { node: Object3D; parent: Object3D }[] = [];
    root.traverse((node) => {
      if (node !== root && isViewportDebris(node)) {
        debris.push({ node, parent: node.parent! });
      }
    });
    for (const { node, parent } of debris) parent.remove(node);

    // Unit-quat hygiene: float64 residue on rest rotations (|q| = 1 + ~1ulp)
    // fails the spec's [-1,1] range at the validator. Normalizing an
    // almost-unit quaternion is semantically the identity — this is a repair
    // of numeric drift, not a pose change, so it is not restored.
    root.traverse((node) => node.quaternion.normalize());
    // Skin-weight hygiene (skin-weight-hygiene.ts): the loader normalizes
    // weights on every open, so the file must already sum to exactly 1 per
    // vertex in the loader's own arithmetic or it drifts an ulp per reopen
    // (the reopen census, 2026-08-27). Same class as the quaternion repair
    // above — numeric drift, not a paint change, not restored.
    const weightHygiene = { vertices: 0, largestNudge: 0, unreachable: 0 };
    root.traverse((node) => {
      if (!(node instanceof SkinnedMesh)) return;
      const attr = node.geometry.getAttribute('skinWeight');
      if (attr === undefined) return;
      const report = exactSumSkinWeights(attr);
      weightHygiene.vertices += report.nudged;
      weightHygiene.largestNudge = Math.max(weightHygiene.largestNudge, report.largestNudge);
      weightHygiene.unreachable += report.unreachable.length;
    });

    const uninstallShim = installFileReaderShim();
    let glb: ArrayBuffer;
    try {
      // `trs: true` — every node's transform written as translation/rotation/
      // scale, never a 4×4 matrix. By default GLTFExporter writes a MATRIX for
      // any node no animation targets, and the loader then decomposes it — a
      // decomposition that lands on the other sign of the same quaternion
      // (measured 2026-08-28 on the fox's Pelvis: a no-clip export of the
      // untouched scene reopened as −q). Same rotation, but the file's rests
      // must not depend on which clips rode along; TRS is what the generators
      // author and what the reopen laws compare.
      const exporter = new GLTFExporter();
      const springs = options.springs ?? [];
      if (springs.length > 0) {
        // ADR-038: the spring chains ride the file as VRMC_springBone 1.0 at
        // the root, keyed by the node indices THIS write assigned (names are
        // resolved after the `_graft` → canonical rename above).
        exporter.register((writer) => ({
          afterParse() {
            const w = writer as unknown as {
              json: { extensions?: Record<string, unknown> };
              extensionsUsed: Record<string, boolean>;
              nodeMap: Map<Object3D, number>;
            };
            let ext;
            try {
              ext = toVrmcSpringBone(springs, (name) => {
                const node = root.getObjectByName(name);
                return node === undefined ? undefined : w.nodeMap.get(node);
              });
            } catch (err) {
              if (err instanceof SpringDeclarationRefusal) throw new GlbExportError([err.message]);
              throw err;
            }
            const extra = options.springExtras ?? {};
            const named = new Set((ext.springs ?? []).map((s) => s.name));
            const stray = Object.keys(extra).filter((n) => !named.has(n));
            if (stray.length > 0) throw new GlbExportError([`spring extras name springs the export does not declare: ${stray.join(', ')}`]);
            for (const s of ext.springs ?? []) {
              const more = s.name === undefined ? undefined : extra[s.name];
              if (more === undefined) continue;
              const poqpoq = ((s.extras ?? {})['poqpoq'] ?? {}) as Record<string, unknown>;
              s.extras = { ...(s.extras ?? {}), poqpoq: { ...poqpoq, ...more } };
            }
            w.json.extensions = { ...(w.json.extensions ?? {}), [VRMC_SPRING_BONE]: ext };
            w.extensionsUsed[VRMC_SPRING_BONE] = true;
          },
        }));
      }
      const out = await exporter.parseAsync(root, { binary: true, trs: true, animations: [...clips] });
      if (!(out instanceof ArrayBuffer)) {
        throw new GlbExportError(['GLTFExporter returned non-binary output despite binary:true']);
      }
      glb = out;
    } finally {
      uninstallShim?.();
      for (const { node, parent } of debris) parent.add(node);
    }

    // ADR-038 §14.5: the surface jiggle's modes ride SPARSE — GLTFExporter writes every
    // morph target dense, and a mode moves one soft mass of the whole body.
    const sparse = sparsifyMorphTargets(glb, (name) => name.startsWith(SURFACE_MORPH_PREFIX));
    glb = sparse.glb;

    // --- structural asserts on the actual bytes ---
    const json = glbJson(glb);
    const sceneCount = json.scenes?.length ?? 0;
    if (sceneCount !== 1) {
      // GLTF_STANDARDS_WATCH: the exporter must never emit a multi-scene GLB.
      throw new GlbExportError([`exported GLB has ${sceneCount} scenes — the contract is exactly one`]);
    }
    const writtenAnimations = json.animations?.length ?? 0;
    if (writtenAnimations !== clips.length) {
      throw new GlbExportError([
        `asked to bake ${clips.length} animation(s) but the file carries ${writtenAnimations} — ` +
          `a track was dropped between validation and write`,
      ]);
    }

    return {
      glb,
      // Ghosts carry no scene-graph name (name-based animation binding must
      // never find them — Codex P2, #98 round eleven), so the report labels
      // them by what they are.
      excluded: debris.map((d) =>
        viewportDebrisLabel(d.node) ?? '<unnamed>',
      ),
      animations: clips.map((c) => ({ name: c.name, tracks: c.tracks.length })),
      renamed: plan.map(({ from, to }) => ({ from, to })),
      weightHygiene,
      sparseMorphs: sparse.report,
    };
  } finally {
    // The LIVE scene belongs to the session — its rig descriptor, markers and
    // retarget reports all speak the session names. Always restored.
    for (const { node, from } of plan) node.name = from;
  }
}
